import * as React from 'react'
import { connect } from 'react-redux'
import { strings } from '../localization'
import { Files, withFilesState } from './with-file-state'
import { RootState } from '../../types/rootstate'
import { setFormValues, setFormValue } from '../../actions/form-values'
import {
  FormValue,
  FormValues,
  FormValueTypes,
  FileFormValue,
  FileTextValue
} from '../../reducers/form-values'
import { build } from './form-type-selector'
import {
  agentIdSelector,
  agentNameSelector
} from '../../selectors/activeconversation'

import '../../sass/grid12.scss'
import '../../sass/forms/nps.scss'
import { FormEntry, Form } from './types'
import { formVersionIdSelector } from '../../selectors/forms'

function setFormProperties(
  versionId: RootState['forms']['key']['versionId'],
  formEntries: FormEntryWithState[]
) {
  const usesHtmlTypes = ['alert', 'paragraph']
  for (const entry of formEntries) {
    if (versionId && usesHtmlTypes.includes(entry.field_type)) {
      entry.usesHtml = true
    }
  }
  return formEntries
}

function execFormRules(
  form: FormEntryWithState[],
  values: RootState['formValues']['key']
) {
  for (const entry of form) {
    const targetForm = form.find(
      (formEntry) => formEntry.qid === entry.rule?.qid
    )
    if (targetForm && values?.[entry.rule.qid]?.type === 'textInput') {
      if (entry.rule?.action === 'show') {
        if (targetForm.field_type === 'checkbox') {
          const checkedValues = (values?.[
            entry.rule.qid
          ] as FileTextValue)?.value.split(',')
          if (
            targetForm.show &&
            checkedValues.indexOf(entry.rule.value) !== -1
          ) {
            entry.show = true
          } else {
            entry.show = false
          }
        } else {
          if (
            !targetForm.show ||
            (values?.[entry.rule.qid] as FileTextValue)?.value !==
              entry.rule.value
          ) {
            entry.show = false
          } else {
            entry.show = true
          }
        }
      } else if (entry.rule?.action === 'hide') {
        if (targetForm.field_type === 'checkbox') {
          const checkedValues = (values?.[
            entry.rule.qid
          ] as FileTextValue)?.value.split(',')
          if (
            !targetForm.show ||
            checkedValues.indexOf(entry.rule.value) !== -1
          ) {
            entry.show = false
          } else {
            entry.show = true
          }
        } else {
          if (
            targetForm.show &&
            (values?.[entry.rule.qid] as FileTextValue)?.value !==
              entry.rule.value
          ) {
            entry.show = true
          } else {
            entry.show = false
          }
        }
      } else {
        entry.show = true
      }
    } else {
      entry.show = true
    }
  }

  return form
}

const addConsentIfNeeded = (
  form: FormEntryWithState[],
  companyName: string
): FormEntryWithState[] => {
  let hasInput = false
  let hasTextarea = false
  let hasSubmit = false
  let submitIndex = -1
  const hasConsent = strings['formsConsent']

  if (!hasConsent) {
    return form
  }

  for (let i = 0; i < form.length; i++) {
    if (form[i].field_type === 'textfield') {
      hasInput = true
    } else if (form[i].field_type === 'textarea') {
      hasTextarea = true
    } else if (form[i].field_type === 'submitButton') {
      hasSubmit = true
      submitIndex = i
    }
  }

  if ((hasInput || hasTextarea) && hasSubmit) {
    const newForm = form.slice()
    const text = strings.formatString(strings['formsConsent'], {
      companyName: companyName
    }) as string
    newForm.splice(submitIndex, 0, {
      field_type: 'consent',
      field_value: 'consentOk',
      label: text,
      qid: 'qConsent',
      required: true,
      requiredMessage: strings.consentRequiredMessage,
      show: true
    })
    return newForm
  } else {
    return form
  }
}

interface DynamicFromPublicProps {
  form
  instanceId: string
  formId: string
  userId: number
  onSubmit: (values: FormValues, fileTokens: string[]) => void
  type: 'preChatForm' | 'customForm'
  companyName: string
}

interface OwnProps extends DynamicFromPublicProps {
  files: Files
  addAndSendFiles: (
    id: string,
    files: Array<File>,
    limit: number
  ) => {
    added: Array<File>
    duplicated: Array<File>
    overLimit: Array<File>
    uploadPromises: Array<Promise<string>>
  }
  removeFile: (id: string, position: number) => void
  hasAtleastOneUploadedFile: (id: string) => boolean
}

interface StateProps {
  values: RootState['formValues']['key']
  agentId: string | number
  agentName: string
  formVersionId: RootState['forms']['key']['versionId']
}

interface DispatchProps {
  setFormValues: (
    instaceId: string,
    formId: string,
    values: FormValue[]
  ) => void
  setFormValue: (
    instanceId: string,
    formId: string,
    id: string,
    value: FormValue
  ) => void
}

type DynamicFromProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState, ownProps): StateProps {
  const key = `${ownProps.instanceId}:${ownProps.formId}`
  return {
    values: state.formValues[key],
    agentId: agentIdSelector(state),
    agentName: agentNameSelector(state),
    formVersionId: formVersionIdSelector(state, ownProps.formId)
  }
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    setFormValues: (instanceId, formId, values): void =>
      dispatch(setFormValues(instanceId, formId, values)),
    setFormValue: (instanceId, formId, id, value): void =>
      dispatch(setFormValue(instanceId, formId, id, value))
  }
}

export type FormEntryWithState = FormEntry & {
  errorMessage?: string
  value?: string[]
  usesHtml?: boolean
  show: boolean
}

interface DynamicFormState {
  form: FormEntryWithState[]
  thankyouText?: string
}

class DynamicFormView extends React.Component<
  DynamicFromProps,
  DynamicFormState
> {
  state: {
    form: FormEntryWithState[]
    thankYouText?: string
  } = {
    form: setFormProperties(
      this.props.formVersionId,
      execFormRules(
        addConsentIfNeeded(this.props.form, this.props.companyName),
        this.getValuesOrDefault(this.props.form, this.props.values)
      )
    ),
    thankYouText: null
  }

  componentDidUpdate(prevProps): void {
    if (prevProps.formId !== this.props.formId) {
      this.setState({
        form: setFormProperties(
          this.props.formVersionId,
          execFormRules(
            addConsentIfNeeded(this.props.form, this.props.companyName),
            this.getValuesOrDefault(this.state.form, this.props.values)
          )
        )
      })
    } else if (prevProps.values !== this.props.values) {
      this.setState({
        form: setFormProperties(
          this.props.formVersionId,
          execFormRules(
            this.state.form,
            this.getValuesOrDefault(this.state.form, this.props.values)
          )
        )
      })
    }
  }

  handleValueChange = (
    id: string,
    type: FormValueTypes,
    value: string
  ): void => {
    const formEntry = this.state.form.find((entry) => entry.qid === id)
    let newState = {}
    if (formEntry?.errorMessage) {
      newState = {
        ...newState,
        form: this.validateAndGetNewForms(id)
      }
    }

    this.setState(newState)

    this.props.setFormValue(this.props.instanceId, this.props.formId, id, {
      type,
      value
    } as FormValue)
  }

  getValuesOrDefault(form: Form, values: FormValues): FormValues {
    return form.reduce((acc, form) => {
      if (values && values[form.qid]) {
        return {
          ...acc,
          [form.qid]: values[form.qid]
        }
      } else {
        let value = ''
        if (form.field_type === 'fileInput') {
          return {
            ...acc,
            [form.qid]: {
              type: 'fileInput',
              tokens: []
            }
          }
        } else if (
          (form.field_type === 'select' || form.field_type === 'radio') &&
          form.field_options &&
          form.field_options.defaultValue
        ) {
          value = form.field_options.defaultValue
        }
        return {
          ...acc,
          [form.qid]: {
            type: 'textInput',
            value
          }
        }
      }
    }, {})
  }

  handleBlur = (id: string): void => {
    this.validateElem(id)
  }

  handleButtonClick = (type: string): void => {
    if (type === 'submitButton') {
      const shouldSubmit = this.isValid()
      if (shouldSubmit) {
        const values = this.getValuesOrDefault(
          this.state.form,
          this.props.values
        )

        const fileTokens = Object.entries(values)
          .filter(([entryId, val]) => val.type === 'fileInput')
          .map(([entryId, val]: [string, FileFormValue]) => val.tokens)
          .reduce((acc, cur) => [...acc, ...cur], [])

        const filteredValues = Object.entries(values).reduce(
          (acc, [key, value]) => {
            const form = this.state.form.find((fe) => fe.qid === key)
            if (form && form.show) {
              return {
                ...acc,
                [key]: value
              }
            } else {
              return acc
            }
          },
          {}
        )

        this.props.onSubmit(filteredValues, fileTokens)
      }
    }
  }

  onFileUploadComplete(id: string, tokens: string[]): void {
    const form = this.state.form
    const values = this.getValuesOrDefault(this.state.form, this.props.values)
    const formValue = values[id] as FileFormValue

    const newValue = [...formValue.tokens, ...tokens]

    const newForm = form.map((entry) => {
      if (entry.qid === id) {
        return {
          ...entry,
          errorMessage: this.props.hasAtleastOneUploadedFile(id)
            ? undefined
            : entry.requiredMessage
        }
      } else {
        return entry
      }
    })

    this.setState({
      form: newForm
    })

    this.props.setFormValue(this.props.instanceId, this.props.formId, id, {
      type: formValue.type,
      tokens: newValue
    } as FileFormValue)
  }

  addFiles = (id: string, files: Array<File>, limit: number): void => {
    const { uploadPromises } = this.props.addAndSendFiles(id, files, limit)
    Promise.all(uploadPromises).then(
      (tokens: string[]) => {
        this.onFileUploadComplete(id, tokens)
      },
      () => {
        // Do nothing, error is handled
      }
    )
  }

  removeFile = (id: string, position: number): void => {
    this.props.removeFile(id, position)

    const form = this.state.form
    const values = this.getValuesOrDefault(this.state.form, this.props.values)
    const formValue = values[id] as FileFormValue
    const currentTokens = formValue.tokens
    const newValue = currentTokens

    if (currentTokens.length - 1 >= position) {
      newValue.splice(position, 1)
    }

    const newForm = form.map((entry) => {
      if (entry.qid === id) {
        return {
          ...entry,
          errorMessage: this.props.hasAtleastOneUploadedFile(id)
            ? undefined
            : entry.requiredMessage
        }
      } else {
        return entry
      }
    })

    this.setState({
      form: newForm
    })

    this.props.setFormValue(this.props.instanceId, this.props.formId, id, {
      type: 'fileInput',
      tokens: newValue
    } as FileFormValue)
  }

  validateWithType(type: string, value: string): boolean {
    if (type === 'containChars10') {
      return value && value.trim().length >= 10
    } else if (type === 'containChars20') {
      return value && value.trim().length >= 20
    } else if (type === 'containChars30') {
      return value && value.trim().length >= 30
    } else if (type === 'containNumbers') {
      return /[0-9]{6,}/.test(value)
    } else if (type === 'email') {
      /* eslint-disable */
      const pattern = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i
      /* eslint-enable */
      return pattern.test(value)
    } else {
      return true
    }
  }

  validateAndGetNewForms(id: string): FormEntryWithState[] {
    const validateForm = this.state.form.find((entry) => entry.qid === id)
    if (validateForm.show) {
      const values = this.getValuesOrDefault(this.state.form, this.props.values)
      const hasValue = !!(values[id] as FileTextValue).value
      if (validateForm.required && !hasValue) {
        validateForm.errorMessage = validateForm.requiredMessage
      } else if (
        (validateForm.field_type === 'textfield' ||
          validateForm.field_type === 'textarea') &&
        validateForm.validationType &&
        hasValue &&
        !this.validateWithType(
          validateForm.validationType,
          (values[id] as FileTextValue).value
        )
      ) {
        if (validateForm.requiredMessageEmail) {
          validateForm.errorMessage = validateForm.requiredMessageEmail
        } else if (validateForm.requiredMessage) {
          validateForm.errorMessage = validateForm.requiredMessage
        } else {
          validateForm.errorMessage = strings.formDefaultValidationError
        }
      } else {
        validateForm.errorMessage = undefined
      }
    }

    return this.state.form.map((form) => {
      if (form.qid === id) {
        return validateForm
      } else {
        return form
      }
    })
  }

  validateElem(id: string): void {
    this.setState({
      form: this.validateAndGetNewForms(id)
    })
  }

  isValid(): boolean {
    let hasAnyError = false
    const values = this.getValuesOrDefault(this.state.form, this.props.values)
    let newForms = this.state.form.map((form) => {
      if (form.show) {
        const hasValue = !!(
          values[form.qid] && (values[form.qid] as FileTextValue).value
        )
        if (form.required && form.field_type === 'fileInput') {
          if (this.props.hasAtleastOneUploadedFile(form.qid)) {
            form.errorMessage = undefined
          } else {
            form.errorMessage = form.requiredMessage
            hasAnyError = true
          }
        } else if (form.required && !hasValue) {
          form.errorMessage = form.requiredMessage
          hasAnyError = true
        } else if (
          (form.field_type === 'textfield' || form.field_type === 'textarea') &&
          form.validationType &&
          hasValue &&
          !this.validateWithType(
            form.validationType,
            (values[form.qid] as FileTextValue).value
          )
        ) {
          hasAnyError = true
          if (form.requiredMessageEmail) {
            form.errorMessage = form.requiredMessageEmail
          } else if (form.requiredMessage) {
            form.errorMessage = form.requiredMessage
          } else {
            form.errorMessage = strings.formDefaultValidationError
          }
        } else {
          form.errorMessage = undefined
        }
      }
      return form
    })

    if (hasAnyError) {
      newForms = newForms.map((form) => {
        if (form.field_type === 'submitButton') {
          form.errorMessage = strings.errorMessageBottom
        }
        return form
      })
      this.setState({
        form: newForms
      })
      return false
    } else {
      return true
    }
  }

  render(): React.ReactNode {
    if (!this.state.form) {
      return null
    } else {
      return build(
        this.props.userId,
        Number(this.props.formId),
        this.state.form,
        this.getValuesOrDefault(this.state.form, this.props.values),
        this.props.files,
        this.handleValueChange,
        this.handleBlur,
        this.handleButtonClick,
        this.addFiles,
        this.removeFile,
        {
          agentId: this.props.agentId,
          agentName: this.props.agentName
        }
      )
    }
  }
}

export const DynamicForm = withFilesState<DynamicFromPublicProps>(
  connect<StateProps, DispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
  )(DynamicFormView)
)
